import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Textarea,
  Heading,
  Text,
  Stack,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  Grid,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import config from '../../config/config';


/**
 * NominationForm component
 * Third step of the application form for nomination questions
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Initial form data
 * @param {Function} props.onChange - Function to call when form data changes
 * @param {boolean} props.showValidation - Whether to show validation errors
 * @param {string} props.id - ID for the form element
 */
const NominationForm = ({ formData = {}, onChange, showValidation = false, id = "nomination-form" }) => {
  const toast = useToast();
  const [selectedBranch, setSelectedBranch] = useState(formData.branch || '');
  const [centersByBranch, setCentersByBranch] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState({
    branches: false,
    centers: false
  });
  const debounceTimer = useRef(null);
  
  // React Hook Form setup
  const {
    register,
    control,
    formState: { errors, isDirty },
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm({
    mode: 'onBlur', // Only validate when focus leaves a field
    defaultValues: {
      branch: formData.branch || '',
      center: formData.center || '',
      readRegulations: formData.readRegulations || false,
      confirmResponsibilities: formData.confirmResponsibilities || false,
      associationKnowledge: formData.associationKnowledge || '',
      socialResponsibility: formData.socialResponsibility || '',
      vision2030Programs: formData.vision2030Programs || '',
      centerIncomeIdeas: formData.centerIncomeIdeas || '',
      acceptTerms: formData.acceptTerms || false,
    },
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  // Function to validate all fields - can be called by parent via ref
  const validateAllFields = async () => {
    const result = await trigger();
    if (!result) {
      toast({
        title: "يرجى تصحيح الأخطاء",
        description: "هناك بعض الحقول التي تحتاج إلى تصحيح",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    return result;
  };
  
  // Custom register function to prevent auto-jumping but allow auto-completion
  const customRegister = (name, validation) => {
    return {
      ...register(name, validation),
      onKeyDown: (e) => {
        // Prevent form submission on Enter key
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }
    };
  };
  
  // Expose form data to parent component
  useEffect(() => {
    if (typeof onChange === 'function') {
      // Pass the current data and validation function to parent
      const validateAndUpdate = async () => {
        const isValid = await validateAllFields();
        return isValid;
      };
      
      // Send initial data and validation function to parent
      onChange(getValues(), false, validateAndUpdate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Only validate when showValidation is true (Next button clicked)
  useEffect(() => {
    if (showValidation) {
      // Manually trigger validation on all fields
      trigger();
    }
  }, [showValidation, trigger]);
  
  // update effect to only apply initial formData on mount, avoiding update loop
  useEffect(() => {
    // Set initial values from props only on mount
    if (Object.keys(formData).length > 0) {
      Object.keys(formData).forEach((key) => setValue(key, formData[key]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for form changes and update parent component
  useEffect(() => {
    const subscription = watch((data) => {
      if (isDirty) {
        // Use a more efficient approach to prevent UI freezing
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        
        // First update with current data immediately
        const currentErrors = Object.keys(errors).length === 0;
        onChange(data, currentErrors);
        
        // Then schedule a full validation
        debounceTimer.current = setTimeout(() => {
          trigger().then(isValid => {
            onChange(data, isValid);
          });
        }, 100);
      }
    });

    // Clean up subscription and debounce
    return () => {
      subscription.unsubscribe();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, isDirty, errors]);

  // Use hardcoded branches data from config file instead of API calls
  useEffect(() => {
    setIsLoading(prev => ({ ...prev, branches: true }));
    
    // Get branches directly from config
    if (config.organization && config.organization.branches && Array.isArray(config.organization.branches)) {
      setBranches(config.organization.branches.map(branch => ({
        id: branch.id,
        name: branch.name,
      })));
    } else {
      console.error('Config branches structure is not as expected');
      setBranches([]);
    }
    
    setIsLoading(prev => ({ ...prev, branches: false }));
  }, []);

  // Handle branch change
  const handleBranchChange = (e) => {
    const branch = e.target.value;
    setSelectedBranch(branch);
    setValue('branch', branch);
    setValue('center', ''); // Reset center when branch changes
    
    // Fetch centers for the selected branch
    fetchCentersByBranch(branch);
  };

  // Get centers by branch ID directly from config
  const fetchCentersByBranch = useCallback((branchId) => {
    if (!branchId) {
      setCentersByBranch([]);
      return;
    }
    
    setIsLoading(prev => ({ ...prev, centers: true }));
    
    // Get centers directly from config and filter by branchId
    if (config.organization && config.organization.centers && Array.isArray(config.organization.centers)) {
      // Filter centers by branchId
      const centersByBranch = config.organization.centers.filter(center => center.branchId === branchId);
      setCentersByBranch(centersByBranch);
    } else {
      console.error('Config centers structure is not as expected');
      setCentersByBranch([]);
    }
    
    setIsLoading(prev => ({ ...prev, centers: false }));
  }, []);

  // Watch branch field for changes
  const watchBranch = watch('branch');

  // Update centers when branch changes
  useEffect(() => {
    if (watchBranch) {
      setSelectedBranch(watchBranch);
      fetchCentersByBranch(watchBranch);
    }
  }, [watchBranch, fetchCentersByBranch]);

  return (
    <Box id={id}>
      <Stack spacing={8}>
        <Box>
          <Heading as="h2" size="md" mb={4}>
            أسئلة الترشيح
          </Heading>
          <Text color="gray.600" mb={6}>
            يرجى الإجابة على الأسئلة التالية المتعلقة بترشيحك
          </Text>
        </Box>

        {/* Branch and Center Selection */}
        <Box mb={8}>
          <Heading as="h3" size="md" mb={4} borderBottom="1px" borderColor="gray.200" pb={2}>
            الفرع والمركز
          </Heading>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={6}>
            {/* Branch Selection */}
            <FormControl isInvalid={errors.branch} isRequired>
              <FormLabel htmlFor="branch">الفرع</FormLabel>
              <Select
                id="branch"
                placeholder={isLoading.branches ? 'جاري التحميل...' : 'اختر الفرع'}
                {...register('branch', {
                  required: 'يرجى اختيار الفرع',
                })}
                onChange={handleBranchChange}
                isDisabled={isLoading.branches}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
              {isLoading.branches && <Spinner size="sm" ml={2} />}
              <FormErrorMessage>
                {errors.branch && errors.branch.message}
              </FormErrorMessage>
            </FormControl>

            {/* Center Selection */}
            <FormControl isInvalid={errors.center} isRequired>
              <FormLabel htmlFor="center">المركز</FormLabel>
              <Select
                id="center"
                placeholder={
                  isLoading.centers
                    ? 'جاري تحميل المراكز...'
                    : !selectedBranch
                    ? 'اختر الفرع أولاً'
                    : centersByBranch.length === 0
                    ? 'لا توجد مراكز متاحة'
                    : 'اختر المركز'
                }
                {...register('center', {
                  required: 'يرجى اختيار المركز',
                })}
                onChange={(e) => {
                  setValue('center', e.target.value);
                  trigger('center');
                }}
                isDisabled={!selectedBranch || isLoading.centers || centersByBranch.length === 0}
              >
                {centersByBranch.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </Select>
              {isLoading.centers && <Spinner size="sm" ml={2} />}
              <FormErrorMessage>
                {errors.center && errors.center.message}
              </FormErrorMessage>
            </FormControl>
          </Grid>


        </Box>

        {/* Regulations Acknowledgment */}
        <FormControl isInvalid={errors.readRegulations} isRequired mb={6}>
          <FormLabel htmlFor="readRegulations">الاطلاع على اللوائح</FormLabel>
          <Controller
            control={control}
            name="readRegulations"
            rules={{ required: 'يجب تأكيد الاطلاع على اللوائح' }}
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                id="readRegulations"
                isChecked={value}
                onChange={(e) => onChange(e.target.checked)}
                ref={ref}
              >
                لقد قرأت واطلعت على لوائح المجلس وأتفهم المسؤوليات المترتبة على العضوية
              </Checkbox>
            )}
          />
          <FormErrorMessage>
            {errors.readRegulations && errors.readRegulations.message}
          </FormErrorMessage>
        </FormControl>

        {/* Responsibilities Confirmation */}
        <FormControl isInvalid={errors.confirmResponsibilities} isRequired mb={6}>
          <FormLabel htmlFor="confirmResponsibilities">تأكيد المسؤوليات</FormLabel>
          <Controller
            control={control}
            name="confirmResponsibilities"
            rules={{ required: 'يجب تأكيد الالتزام بالمسؤوليات' }}
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                id="confirmResponsibilities"
                isChecked={value}
                onChange={(e) => onChange(e.target.checked)}
                ref={ref}
              >
                أؤكد التزامي بحضور اجتماعات المجلس والمشاركة الفعالة في أنشطته
              </Checkbox>
            )}
          />
          <FormErrorMessage>
            {errors.confirmResponsibilities && errors.confirmResponsibilities.message}
          </FormErrorMessage>
        </FormControl>

        {/* Association Knowledge */}
        <FormControl isInvalid={errors.associationKnowledge} isRequired mb={6}>
          <FormLabel htmlFor="associationKnowledge">المعرفة بالجمعية</FormLabel>
          <Textarea
            id="associationKnowledge"
            placeholder="ماذا تعرف عن جمعية مراكز الأحياء بمنطقة مكة المكرمة"
            rows={4}
            {...customRegister('associationKnowledge', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.associationKnowledge && errors.associationKnowledge.message}
          </FormErrorMessage>
        </FormControl>

        {/* Social Responsibility */}
        <FormControl isInvalid={errors.socialResponsibility} isRequired mb={6}>
          <FormLabel htmlFor="socialResponsibility">المسؤولية الاجتماعية</FormLabel>
          <Textarea
            id="socialResponsibility"
            placeholder="ماهي مشاركاتك في جانب المسؤولية الاجتماعية"
            rows={4}
            {...customRegister('socialResponsibility', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.socialResponsibility && errors.socialResponsibility.message}
          </FormErrorMessage>
        </FormControl>

        {/* Vision 2030 Programs */}
        <FormControl isInvalid={errors.vision2030Programs} isRequired mb={6}>
          <FormLabel htmlFor="vision2030Programs">دورك في تحقيق رؤية 2030</FormLabel>
          <Textarea
            id="vision2030Programs"
            placeholder="ما هو الدور الذي ترى أنك ستقوم به كمرشح لتحقيق أهداف وبرامج ومبادرات الجمعية بما يتوافق مع رؤية المملكة 2030"
            rows={4}
            {...customRegister('vision2030Programs', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormHelperText>
            فضلاً اذكر مقترحات أو أفكار عملية يمكنك المساهمة بها في المجلس لخدمة الحي وتعزيز دور الجمعية
          </FormHelperText>
          <FormErrorMessage>
            {errors.vision2030Programs && errors.vision2030Programs.message}
          </FormErrorMessage>
        </FormControl>

        {/* Center Income Ideas */}
        <FormControl isInvalid={errors.centerIncomeIdeas} isRequired mb={6}>
          <FormLabel htmlFor="centerIncomeIdeas">تنمية الموارد المالية</FormLabel>
          <Textarea
            id="centerIncomeIdeas"
            placeholder="ماهي خطتك لتفعيل مجلس الحي وتنمية الموارد المالية"
            rows={4}
            {...customRegister('centerIncomeIdeas', {
              required: 'هذا الحقل مطلوب',
              minLength: {
                value: 50,
                message: 'يجب أن تكون الإجابة على الأقل 50 حرف',
              },
              maxLength: {
                value: 3000,
                message: 'يجب أن تكون الإجابة أقل من 3000 حرف',
              },
            })}
          />
          <FormErrorMessage>
            {errors.centerIncomeIdeas && errors.centerIncomeIdeas.message}
          </FormErrorMessage>
        </FormControl>

        {/* Terms and Conditions */}
        <FormControl isInvalid={errors.acceptTerms} isRequired>
          <FormLabel htmlFor="acceptTerms">الشروط والأحكام</FormLabel>
          <Controller
            control={control}
            name="acceptTerms"
            rules={{ required: 'يجب الموافقة على الشروط والأحكام' }}
            render={({ field: { onChange, value, ref } }) => (
              <RadioGroup 
                onChange={(val) => {
                  onChange(val === 'true');
                  // Force validation update immediately
                  setTimeout(() => trigger('acceptTerms'), 0);
                }} 
                value={value ? 'true' : 'false'}
              >
                <Stack direction="column" spacing={2} align="start">
                  <Radio value="true">
                    الإقرار و الموافقة على الشروط والأحكام وأقر بأنني:
                  </Radio>
                  <Box pr={8} pt={1}>
                    <Text mb={1}>• من ذوي السمعة الحسنة</Text>
                    <Text mb={1}>• غير محكوم علي بإدانة في جريمة مخلة بالشرف أو الأمانة مالم يكن قد رد إلي اعتباري</Text>
                    <Text>• كامل الأهلية المعتبرة شرعاً</Text>
                  </Box>
                </Stack>
              </RadioGroup>
            )}
          />
          <FormErrorMessage>
            {errors.acceptTerms && errors.acceptTerms.message}
          </FormErrorMessage>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default NominationForm;
